/**
 * stylesPending.js — 미팅지도 피드백미입력·서브옵션 스타일
 *
 * 포함 스타일:
 *   - 피드백 미입력 뱃지 버튼 (종 아이콘 + 카운트 뱃지)
 *   - 피드백 필요 미팅 아이템 카드
 *   - 미팅인계 / 다음일정 서브 옵션 (날짜 지정·담당자 선택 등)
 */

// ── 피드백 미입력 뱃지 버튼 ────────────────────────────────────────────────────

/** 뱃지 버튼 컨테이너: 종 아이콘 + 우측 상단 카운트 오버레이 */
export const pendingBadgeBtnStyle = {
  position: 'relative',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  padding: '2px 4px',
  lineHeight: 1,
  display: 'inline-flex',
  alignItems: 'center',
};

/** 종 아이콘 */
export const pendingBellIconStyle = {
  fontSize: 20,
  lineHeight: 1,
};

/** 빨간 카운트 뱃지: 우측 상단에 겹쳐서 표시 (카카오톡 스타일) */
export const pendingCountBadgeStyle = {
  position: 'absolute',
  top: -2,
  right: -6,
  background: 'var(--mm-tone-red-solid-bg)',
  color: 'var(--mm-tone-red-solid-text)',
  borderRadius: 999,
  fontSize: 10,
  fontWeight: 800,
  minWidth: 16,
  height: 16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 3px',
  lineHeight: 1,
  pointerEvents: 'none',
};

/** 모달 내 빈 상태 (모든 피드백 완료) */
export const pendingEmptyStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 8,
  padding: '28px 0',
  color: 'var(--text-muted)',
  fontSize: 14,
  fontWeight: 600,
};

// ── 피드백 필요 미팅 아이템 카드 ─────────────────────────────────────────────

/** 피드백 필요 미팅 아이템 카드 */
export const pendingItemStyle = {
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: '10px 12px',
  background: 'var(--bg-card)',
  display: 'grid',
  gap: 6,
};

/** 아이템 상단: 날짜 / 시간 / 상품 뱃지 행 */
export const pendingItemTopStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 5,
  alignItems: 'center',
};

/** 날짜 뱃지 (연한 회색) */
export const pendingItemDateStyle = {
  fontSize: 11,
  fontWeight: 700,
  color: 'var(--text-muted)',
  background: 'var(--bg-subtle)',
  borderRadius: 4,
  padding: '2px 6px',
};

/** 시간 텍스트 */
export const pendingItemTimeStyle = {
  fontSize: 11,
  color: 'var(--text-muted)',
  fontWeight: 500,
};

/** 상품 뱃지 (인디고 pill) */
export const pendingItemTicketStyle = {
  fontSize: 11,
  fontWeight: 700,
  background: 'var(--mm-tone-blue-bg)',
  color: 'var(--mm-tone-blue-text)',
  border: '1px solid var(--mm-tone-blue-border)',
  borderRadius: 999,
  padding: '2px 7px',
};

/** 담당 TM 텍스트 */
export const pendingItemTmStyle = {
  fontSize: 11,
  color: 'var(--text-muted)',
  fontWeight: 600,
};

/** 아이템 하단: 상호명 + 결과 입력 버튼 */
export const pendingItemBottomStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 8,
};

/** 상호명 텍스트: 말줄임 */
export const pendingItemCompanyStyle = {
  fontSize: 14,
  fontWeight: 800,
  color: 'var(--text-sub)',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

// ── 미팅인계 / 다음일정 서브 옵션 ────────────────────────────────────────────

/**
 * 미뤄짐 서브탭: 날짜 지정 / 날짜 미정 토글 버튼
 * @param {boolean} active 활성 여부
 */
export const postponeTabBtnStyle = (active) => ({
  flex: 1,
  border: `1px solid ${active ? 'var(--mm-tone-amber-solid-border)' : 'var(--border)'}`,
  borderRadius: 8,
  padding: '7px 0',
  fontSize: 12,
  fontWeight: active ? 700 : 500,
  background: active ? 'var(--mm-tone-amber-solid-bg)' : 'var(--bg-card)',
  color: active ? 'var(--mm-tone-amber-solid-text)' : 'var(--text-muted)',
  cursor: 'pointer',
  transition: 'all 0.15s',
});

/** 내일/모레 빠른날짜 버튼 */
export const quickDateBtnStyle = {
  border: '1px solid var(--border)',
  borderRadius: 6,
  padding: '6px 10px',
  fontSize: 12,
  fontWeight: 600,
  background: 'var(--bg-card)',
  color: 'var(--text-sub)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

/** 서브 옵션 래퍼: 깔끔한 light gray 카드 */
export const subOptionWrapStyle = {
  background: 'var(--bg-subtle)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: '12px 14px',
  display: 'grid',
  gap: 8,
};

/** 마케팅 담당자 선택 그리드 (flex wrap) */
export const salesPersonGridStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
};

/**
 * 담당자 버튼: 선택 시 solid blue / 기본 outline
 * @param {boolean} isSelected 선택 여부
 */
export const salesPersonBtnStyle = (isSelected) => ({
  border: `1px solid ${isSelected ? 'var(--mm-tone-blue-solid-border)' : 'var(--border)'}`,
  borderRadius: 6,
  padding: '5px 12px',
  fontSize: 12,
  fontWeight: isSelected ? 600 : 500,
  cursor: 'pointer',
  background: isSelected ? 'var(--mm-tone-blue-solid-bg)' : 'var(--bg-card)',
  color: isSelected ? 'var(--mm-tone-blue-solid-text)' : 'var(--text-sub)',
  transition: 'all 0.1s',
});

/** 선택 결과 안내 텍스트 */
export const subOptionSelectedStyle = {
  fontSize: 12,
  color: 'var(--text-sub)',
  fontWeight: 500,
  marginTop: 2,
};

/** 목록이 비었을 때 "왜 비었는지" 알려주는 안내 (2026-09-09)
 *   빈 칸만 남기면 사용자가 원인을 알 수 없어 같은 시도를 반복한다. */
export const emptyHintStyle = {
  fontSize: 12,
  color: 'var(--text-muted)',
  lineHeight: 1.6,
  padding: '8px 10px',
  background: 'var(--bg-subtle)',
  borderRadius: 8,
  marginTop: 2,
};

/** 저장 전 주의 안내 (미팅인계 담당자 미선택 등) */
export const warnHintStyle = {
  ...emptyHintStyle,
  color: 'var(--mm-tone-amber-text)',
  background: 'var(--mm-tone-amber-bg)',
  border: '1px solid var(--mm-tone-amber-border)',
};

/** 결과 입력 버튼 (아이템 우측, 보라색 pill) */
export const pendingItemEntryBtnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  border: '1px solid var(--mm-tone-purple-border)',
  borderRadius: 8,
  background: 'var(--mm-tone-purple-bg)',
  color: 'var(--mm-tone-purple-text)',
  fontSize: 12,
  fontWeight: 700,
  padding: '5px 10px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};
